import React, { useRef, useState, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import QRCode from 'qrcode';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import './CustomizableBill.css';

const CustomizableBill = ({ order, outlet }) => {
  const billRef = useRef();
  const [billSettings, setBillSettings] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Load bill settings
  useEffect(() => {
    const loadBillSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'billFormat'));
        if (settingsDoc.exists()) {
          setBillSettings(settingsDoc.data());
        } else {
          // Default settings
          setBillSettings({
            showLogo: true,
            showGST: true,
            showFSSAI: true,
            showQRCode: true,
            gstNumber: 'GST-123456WERTYU',
            fssaiNumber: 'FSSAI-ABC123ARPIT'
          });
        }
      } catch (error) {
        console.error('Error loading bill settings:', error);
      }
    };

    loadBillSettings();
  }, []);

  // Generate UPI QR Code
  useEffect(() => {
    if (billSettings?.showQRCode && order?.total) {
      const upiString = `upi://pay?pa=${billSettings.upiId || 'merchant@upi'}&pn=${
        outlet?.name || 'Restaurant'
      }&am=${order.total.toFixed(2)}&cu=INR&tn=Order ${order.orderNumber}`;

      QRCode.toDataURL(upiString, { width: 200, margin: 1 })
        .then(url => setQrCodeUrl(url))
        .catch(err => console.error('QR Code generation error:', err));
    }
  }, [billSettings, order, outlet]);

  const handlePrint = useReactToPrint({
    content: () => billRef.current,
    documentTitle: `Bill-${order.orderNumber}`,
  });

  if (!billSettings) {
    return <div>Loading...</div>;
  }

  return (
    <div className="bill-container">
      <button className="print-bill-btn" onClick={handlePrint}>
        🖨️ Print Bill
      </button>

      <div className="bill-preview" ref={billRef}>
        {/* Header */}
        <div className="bill-header">
          {billSettings.showLogo && (
            <div className="bill-logo">
              {outlet?.logo ? (
                <img src={outlet.logo} alt={outlet.name} />
              ) : (
                <div className="bill-logo-placeholder">
                  YOUR LOGO
                </div>
              )}
            </div>
          )}

          <h1 className="bill-restaurant-name">{outlet?.name || 'Acme Restaurant'}</h1>

          {billSettings.showGST && (
            <p className="bill-info-line">
              <strong>GST:</strong> {billSettings.gstNumber}
            </p>
          )}

          {billSettings.showFSSAI && (
            <p className="bill-info-line">
              <strong>FSSAI:</strong> {billSettings.fssaiNumber}
            </p>
          )}

          <p className="bill-info-line">{outlet?.address}</p>
          <p className="bill-info-line">{outlet?.phone}</p>
        </div>

        <div className="bill-divider">
          <h2 className="bill-title">Retail Invoice</h2>
        </div>

        {/* Order Details */}
        <div className="bill-details">
          <div className="bill-detail-row">
            <span>Token no.</span>
            <span className="bill-token">{order.tokenNumber || '7'}</span>
          </div>
          <div className="bill-detail-row">
            <span>Date</span>
            <span>
              {order.createdAt?.toDate
                ? new Date(order.createdAt.toDate()).toLocaleString()
                : new Date().toLocaleString()}
            </span>
          </div>
          <div className="bill-detail-row">
            <span>Table</span>
            <span>AC {order.tableNumber}</span>
          </div>
        </div>

        {/* Items Table */}
        <table className="bill-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Amt</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((item, idx) => (
              <tr key={idx}>
                <td className="bill-item-name">
                  {idx + 1}. {item.name}
                </td>
                <td>{item.quantity}</td>
                <td>₹{item.price}</td>
                <td>₹{(item.price * item.quantity).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="bill-totals">
          <div className="bill-total-row">
            <span>Sub-total:</span>
            <span>₹{order.subtotal?.toFixed(2) || '0.00'}</span>
          </div>
          {order.discount > 0 && (
            <div className="bill-total-row discount">
              <span>Discount:</span>
              <span>-₹{order.discount?.toFixed(2)}</span>
            </div>
          )}
          <div className="bill-total-row">
            <span>Tax (5%):</span>
            <span>₹{order.tax?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="bill-total-row grand-total">
            <span>Grand Total:</span>
            <span>₹{order.total?.toFixed(2) || '0.00'}</span>
          </div>
        </div>

        {/* QR Code */}
        {billSettings.showQRCode && qrCodeUrl && (
          <div className="bill-qr-section">
            <h3>UPI Payment QR Code</h3>
            <img src={qrCodeUrl} alt="UPI QR Code" className="bill-qr-code" />
            <p className="bill-qr-text">Scan to pay ₹{order.total?.toFixed(2)}</p>
          </div>
        )}

        {/* Footer */}
        <div className="bill-footer">
          <p className="bill-thank-you">Thank you for your visit!</p>
          <p className="bill-footer-note">Please visit again</p>
        </div>
      </div>
    </div>
  );
};

export default CustomizableBill;
